import type { ComponentProps, FC, PropsWithChildren } from "react";

import { CheckCircle, Warning } from "@similarweb/icons";
import { CircularLoader, Tooltip } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import {
    COLOR_BY_CHIP_TYPE,
    STATUS_CHIP_LOADER_OPTIONS,
    StyledStatusChip,
    StyledStatusChipLoaderWrapper,
} from "./styles";
import type { StatusChipType } from "./types";

type IconProps = ComponentProps<typeof CheckCircle>;

export const CHIP_ICON_BY_CHIP_TYPE: Partial<Record<StatusChipType, FC<IconProps>>> = {
    success: CheckCircle,
    error: Warning,
};

export interface IStatusChipProps {
    chipType?: StatusChipType;
    isLoading?: boolean;
    tooltipText?: string | string[];
    dataAutomation?: string;
    ariaLive?: "off" | "polite" | "assertive";
    id?: string;
}

export const StatusChip = ({
    chipType = "success",
    isLoading,
    tooltipText,
    dataAutomation,
    ariaLive,
    id,
    children,
}: PropsWithChildren<IStatusChipProps>) => {
    const IconComponent = CHIP_ICON_BY_CHIP_TYPE[chipType];
    const tooltipItems: string[] = !tooltipText ? [] : Array.isArray(tooltipText) ? tooltipText : [tooltipText];
    const isTooltipEnabled = !!tooltipItems.length;

    const tooltipContent = (
        <Flex
            flexDirection="column"
            gap="8px"
        >
            {tooltipItems.map((text, index) => (
                <div key={index}>{text}</div>
            ))}
        </Flex>
    );

    return (
        <Tooltip
            enabled={isTooltipEnabled}
            content={tooltipContent}
        >
            <StyledStatusChip
                type={chipType}
                id={id}
                aria-live={ariaLive}
                data-automation={dataAutomation}
            >
                {IconComponent && !isLoading ? (
                    <IconComponent
                        size="xs"
                        color={COLOR_BY_CHIP_TYPE[chipType]}
                    />
                ) : null}

                {isLoading ? (
                    <StyledStatusChipLoaderWrapper type={chipType}>
                        <CircularLoader options={STATUS_CHIP_LOADER_OPTIONS} />
                    </StyledStatusChipLoaderWrapper>
                ) : null}

                <Text
                    fontVariant="listItem3"
                    color={COLOR_BY_CHIP_TYPE[chipType]}
                >
                    {children}
                </Text>
            </StyledStatusChip>
        </Tooltip>
    );
};
